import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import {
  DocumentTextIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  InformationCircleIcon,
  ArrowUpTrayIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

interface TaxProfile {
  id: string;
  user_id: string;
  tax_year: number;
  filing_status: string;
  dependents: number;
  is_self_employed: boolean;
  estimated_annual_income: number;
  created_at: string;
  updated_at: string;
}

interface IncomeEntry {
  id: string;
  user_id: string;
  tax_year: number;
  income_type: string;
  description: string;
  amount: number;
  payer_name?: string;
  is_taxable: boolean;
  withholding: number;
  created_at: string;
}

interface DeductionEntry {
  id: string;
  user_id: string;
  tax_year: number;
  category: string;
  description: string;
  amount: number;
  is_above_the_line: boolean;
  documentation_status: string;
  created_at: string;
}

interface TaxEstimate {
  tax_year: number;
  filing_status: string;
  total_income: number;
  above_the_line_deductions: number;
  agi: number;
  standard_deduction: number;
  itemized_deductions: number;
  using_itemized: boolean;
  taxable_income: number;
  federal_tax: number;
  self_employment_tax: number;
  total_tax: number;
  effective_tax_rate: number;
  marginal_bracket: string;
}

interface QuarterlyEstimate {
  quarter: number;
  due_date: string;
  amount_due: number;
  is_past_due: boolean;
}

interface DeductionCategory {
  id: string;
  name: string;
  description: string;
  is_above_the_line: boolean;
  examples: string[];
}

interface FilingStatus {
  id: string;
  name: string;
  standard_deduction: number;
}

// Editable estimate values for user modifications
interface EditableEstimate {
  filing_status: string;
  gross_income: number;
  above_the_line_deductions: number;
  itemized_deductions: number;
  use_itemized: boolean;
  federal_withholding: number;
  state_withholding: number;
  estimated_payments: number;
  self_employment_income: number;
  // Computed fields (calculated from above)
  agi: number;
  taxable_income: number;
  federal_tax: number;
  self_employment_tax: number;
  total_tax: number;
  effective_rate: number;
  marginal_bracket: string;
  balance_due: number;
}

const TaxPlanning: React.FC = () => {
  const { t } = useTranslation('taxPlanning');

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'income' | 'deductions' | 'estimate'>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [profile, setProfile] = useState<TaxProfile | null>(null);
  const [incomeEntries, setIncomeEntries] = useState<IncomeEntry[]>([]);
  const [deductionEntries, setDeductionEntries] = useState<DeductionEntry[]>([]);
  const [taxEstimate, setTaxEstimate] = useState<TaxEstimate | null>(null);
  const [quarterlyEstimates, setQuarterlyEstimates] = useState<QuarterlyEstimate[]>([]);
  const [documentChecklist, setDocumentChecklist] = useState<Record<string, any>>({});

  // Reference data
  const [filingStatuses, setFilingStatuses] = useState<FilingStatus[]>([]);
  const [deductionCategories, setDeductionCategories] = useState<DeductionCategory[]>([]);

  // Modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Edit mode state - tracks which entry is being edited (null = add mode)
  const [editingIncomeId, setEditingIncomeId] = useState<string | null>(null);
  const [editingDeductionId, setEditingDeductionId] = useState<string | null>(null);

  // Export removed - using ExportDropdownMenu inline

  // Editable estimate state - allows user to modify values for "what-if" scenarios
  const [editableEstimate, setEditableEstimate] = useState<EditableEstimate | null>(null);
  const [isEditingEstimate, setIsEditingEstimate] = useState(false);
  const [estimateModified, setEstimateModified] = useState(false);

  // Loading overlay state for long-running operations
  const [loadingOverlay, setLoadingOverlay] = useState<{
    visible: boolean;
    message: string;
    subMessage?: string;
  }>({ visible: false, message: '' });

  // File input ref for uploads
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [profileForm, setProfileForm] = useState({
    tax_year: new Date().getFullYear(),
    filing_status: 'single',
    dependents: 0,
    is_self_employed: false,
    estimated_annual_income: 0,
  });

  const [incomeForm, setIncomeForm] = useState({
    income_type: 'w2_wages',
    description: '',
    amount: 0,
    payer_name: '',
    is_taxable: true,
    withholding: 0,
  });

  const [deductionForm, setDeductionForm] = useState({
    category: '',
    description: '',
    amount: 0,
  });

  // Load data on mount
  useEffect(() => {
    loadReferenceData();
    loadProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      loadIncomeEntries();
      loadDeductionEntries();
      loadTaxEstimate();
      loadDocumentChecklist();
    }
  }, [profile]);

  const loadReferenceData = async () => {
    try {
      const [statusesRes, categoriesRes] = await Promise.all([
        apiClient.get('/tax-planning/filing-statuses'),
        apiClient.get('/tax-planning/deduction-categories'),
      ]);
      setFilingStatuses(statusesRes.data.filing_statuses || []);
      setDeductionCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await apiClient.get('/tax-planning/profile');
      // Backend returns { profile: {...} }, extract the profile object
      if (response.data?.profile) {
        setProfile(response.data.profile);
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Failed to load profile:', err);
      }
    }
  };

  const loadIncomeEntries = async () => {
    if (!profile) return;
    try {
      const response = await apiClient.get(`/tax-planning/income?tax_year=${profile.tax_year}`);
      setIncomeEntries(response.data.entries || []);
    } catch (err) {
      console.error('Failed to load income entries:', err);
    }
  };

  const loadDeductionEntries = async () => {
    if (!profile) return;
    try {
      const response = await apiClient.get(`/tax-planning/deductions?tax_year=${profile.tax_year}`);
      setDeductionEntries(response.data.entries || []);
    } catch (err) {
      console.error('Failed to load deduction entries:', err);
    }
  };

  const loadTaxEstimate = async () => {
    if (!profile) return;
    try {
      // Use /summary endpoint which calculates estimate from stored income/deductions
      const response = await apiClient.get('/tax-planning/summary');
      const data = response.data;

      // Map summary response to TaxEstimate interface
      const estimate: TaxEstimate = {
        tax_year: profile.tax_year,
        filing_status: data.profile?.filing_status || 'single',
        total_income: data.income_summary?.total || 0,
        above_the_line_deductions: 0, // Summary doesn't track above-the-line separately
        agi: (data.income_summary?.total || 0), // AGI = total income - above-the-line deductions
        standard_deduction: data.deduction_summary?.standard || 0,
        itemized_deductions: data.deduction_summary?.total_itemized || 0,
        using_itemized: data.deduction_summary?.using === 'itemized',
        taxable_income: data.tax_estimate?.taxable_income || 0,
        federal_tax: data.tax_estimate?.income_tax || 0,
        self_employment_tax: data.tax_estimate?.self_employment_tax || 0,
        total_tax: data.tax_estimate?.total_tax || 0,
        effective_tax_rate: data.tax_estimate?.effective_rate || 0,
        marginal_bracket: `${data.tax_estimate?.marginal_rate || 0}%`,
      };
      setTaxEstimate(estimate);
    } catch (err) {
      console.error('Failed to load tax estimate:', err);
    }
  };

  const loadDocumentChecklist = async () => {
    if (!profile) return;
    try {
      // Fetch both document checklist and deadlines
      const [docsResponse, deadlinesResponse] = await Promise.all([
        apiClient.get('/tax-planning/document-checklist'),
        apiClient.get('/tax-planning/deadlines'),
      ]);

      // Transform documents array into categorized checklist object
      const documents = docsResponse.data.documents || [];
      const checklist: Record<string, string[]> = {};
      documents.forEach((doc: any) => {
        const category = doc.category || 'other';
        if (!checklist[category]) {
          checklist[category] = [];
        }
        checklist[category].push(doc.name);
      });

      // Get deadlines and format them
      const deadlines = (deadlinesResponse.data.deadlines || []).map((d: any) => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        description: d.description,
        is_past: d.is_past,
      }));

      setDocumentChecklist({ checklist, deadlines });
    } catch (err) {
      console.error('Failed to load document checklist:', err);
    }
  };

  const loadQuarterlyEstimates = async () => {
    if (!profile) return;
    try {
      // Get summary data to calculate quarterly estimates
      const summaryResponse = await apiClient.get('/tax-planning/summary');
      const summary = summaryResponse.data;

      // Determine current quarter (1-4)
      const currentMonth = new Date().getMonth() + 1;
      const currentQuarter = Math.ceil(currentMonth / 3);

      // Calculate quarterly estimates using proper fields
      const response = await apiClient.post('/tax-planning/quarterly-estimate', {
        filing_status: profile.filing_status,
        annual_income_estimate: summary.income_summary?.total || 0,
        self_employment_income: summary.income_summary?.self_employment || 0,
        withholding_estimate: summary.income_summary?.withholding || 0,
        current_quarter: currentQuarter,
      });

      // Transform backend response to frontend QuarterlyEstimate[] format
      const data = response.data;
      const paymentPerQuarter = data.quarterly_estimate?.payment_per_quarter || 0;
      const taxYear = profile.tax_year;
      const today = new Date();

      // Quarterly due dates for the tax year
      const quarterDueDates = [
        { quarter: 1, dueDate: new Date(taxYear, 3, 15) }, // April 15
        { quarter: 2, dueDate: new Date(taxYear, 5, 15) }, // June 15
        { quarter: 3, dueDate: new Date(taxYear, 8, 15) }, // September 15
        { quarter: 4, dueDate: new Date(taxYear + 1, 0, 15) }, // January 15 of next year
      ];

      const quarters: QuarterlyEstimate[] = quarterDueDates.map(({ quarter, dueDate }) => ({
        quarter,
        due_date: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        amount_due: paymentPerQuarter,
        is_past_due: quarter < currentQuarter || (dueDate < today && quarter === currentQuarter),
      }));

      setQuarterlyEstimates(quarters);
    } catch (err) {
      console.error('Failed to load quarterly estimates:', err);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/tax-planning/profile', profileForm);
      setProfile(response.data);
      setShowProfileModal(false);
    } catch (err: any) {
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'field';
          return `${field}: ${e.msg}`;
        });
        setError(messages.join('; ') || t('errors.saveFailed'));
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || JSON.stringify(detail) || t('errors.saveFailed'));
      } else {
        setError(detail || t('errors.saveFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddIncome = async () => {
    if (!profile) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/tax-planning/income', {
        ...incomeForm,
        tax_year: profile.tax_year,
      });
      await loadIncomeEntries();
      await loadTaxEstimate();
      setShowIncomeModal(false);
      setIncomeForm({
        income_type: 'w2_wages',
        description: '',
        amount: 0,
        payer_name: '',
        is_taxable: true,
        withholding: 0,
      });
    } catch (err: any) {
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Extract meaningful message from Pydantic validation errors
        const messages = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'field';
          return `${field}: ${e.msg}`;
        });
        setError(messages.join('; ') || t('errors.addFailed'));
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || JSON.stringify(detail) || t('errors.addFailed'));
      } else {
        setError(detail || t('errors.addFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIncome = async (id: string) => {
    if (!window.confirm(t('confirmDeleteIncome'))) return;
    try {
      await apiClient.delete(`/tax-planning/income/${id}`);
      await loadIncomeEntries();
      await loadTaxEstimate();
    } catch (err) {
      console.error('Failed to delete income:', err);
    }
  };

  const handleEditIncome = (entry: IncomeEntry) => {
    setEditingIncomeId(entry.id);
    setIncomeForm({
      income_type: entry.income_type,
      description: entry.description,
      amount: entry.amount,
      payer_name: entry.payer_name || '',
      is_taxable: entry.is_taxable,
      withholding: entry.withholding || 0,
    });
    setShowIncomeModal(true);
  };

  const handleUpdateIncome = async () => {
    if (!editingIncomeId) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.put(`/tax-planning/income/${editingIncomeId}`, {
        ...incomeForm,
        tax_year: profile?.tax_year || new Date().getFullYear(),
      });
      await loadIncomeEntries();
      await loadTaxEstimate();
      setShowIncomeModal(false);
      setEditingIncomeId(null);
      setIncomeForm({
        income_type: 'w2_wages',
        description: '',
        amount: 0,
        payer_name: '',
        is_taxable: true,
        withholding: 0,
      });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'field';
          return `${field}: ${e.msg}`;
        });
        setError(messages.join('; ') || t('errors.saveFailed'));
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || JSON.stringify(detail) || t('errors.saveFailed'));
      } else {
        setError(detail || t('errors.saveFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeduction = async () => {
    if (!profile) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/tax-planning/deductions', {
        ...deductionForm,
        tax_year: profile.tax_year,
      });
      await loadDeductionEntries();
      await loadTaxEstimate();
      setShowDeductionModal(false);
      setDeductionForm({
        category: '',
        description: '',
        amount: 0,
      });
    } catch (err: any) {
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'field';
          return `${field}: ${e.msg}`;
        });
        setError(messages.join('; ') || t('errors.addFailed'));
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || JSON.stringify(detail) || t('errors.addFailed'));
      } else {
        setError(detail || t('errors.addFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeduction = async (id: string) => {
    if (!window.confirm(t('confirmDeleteDeduction'))) return;
    try {
      await apiClient.delete(`/tax-planning/deductions/${id}`);
      await loadDeductionEntries();
      await loadTaxEstimate();
    } catch (err) {
      console.error('Failed to delete deduction:', err);
    }
  };

  const handleEditDeduction = (entry: DeductionEntry) => {
    setEditingDeductionId(entry.id);
    setDeductionForm({
      category: entry.category,
      description: entry.description,
      amount: entry.amount,
    });
    setShowDeductionModal(true);
  };

  const handleUpdateDeduction = async () => {
    if (!editingDeductionId) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.put(`/tax-planning/deductions/${editingDeductionId}`, {
        ...deductionForm,
        tax_year: profile?.tax_year || new Date().getFullYear(),
      });
      await loadDeductionEntries();
      await loadTaxEstimate();
      setShowDeductionModal(false);
      setEditingDeductionId(null);
      setDeductionForm({
        category: '',
        description: '',
        amount: 0,
      });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'field';
          return `${field}: ${e.msg}`;
        });
        setError(messages.join('; ') || t('errors.saveFailed'));
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || JSON.stringify(detail) || t('errors.saveFailed'));
      } else {
        setError(detail || t('errors.saveFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getFilingStatusName = (status: string) => {
    const found = filingStatuses.find(s => s.id === status);
    return found?.name || status;
  };

  // Calculate federal tax based on 2024 tax brackets
  const calculateFederalTax = (taxableIncome: number, filingStatus: string): { tax: number; bracket: string } => {
    // 2024 tax brackets
    const brackets: Record<string, { rate: number; min: number; max: number }[]> = {
      single: [
        { rate: 0.10, min: 0, max: 11600 },
        { rate: 0.12, min: 11600, max: 47150 },
        { rate: 0.22, min: 47150, max: 100525 },
        { rate: 0.24, min: 100525, max: 191950 },
        { rate: 0.32, min: 191950, max: 243725 },
        { rate: 0.35, min: 243725, max: 609350 },
        { rate: 0.37, min: 609350, max: Infinity },
      ],
      married_joint: [
        { rate: 0.10, min: 0, max: 23200 },
        { rate: 0.12, min: 23200, max: 94300 },
        { rate: 0.22, min: 94300, max: 201050 },
        { rate: 0.24, min: 201050, max: 383900 },
        { rate: 0.32, min: 383900, max: 487450 },
        { rate: 0.35, min: 487450, max: 731200 },
        { rate: 0.37, min: 731200, max: Infinity },
      ],
      married_separate: [
        { rate: 0.10, min: 0, max: 11600 },
        { rate: 0.12, min: 11600, max: 47150 },
        { rate: 0.22, min: 47150, max: 100525 },
        { rate: 0.24, min: 100525, max: 191950 },
        { rate: 0.32, min: 191950, max: 243725 },
        { rate: 0.35, min: 243725, max: 365600 },
        { rate: 0.37, min: 365600, max: Infinity },
      ],
      head_of_household: [
        { rate: 0.10, min: 0, max: 16550 },
        { rate: 0.12, min: 16550, max: 63100 },
        { rate: 0.22, min: 63100, max: 100500 },
        { rate: 0.24, min: 100500, max: 191950 },
        { rate: 0.32, min: 191950, max: 243700 },
        { rate: 0.35, min: 243700, max: 609350 },
        { rate: 0.37, min: 609350, max: Infinity },
      ],
    };

    const statusBrackets = brackets[filingStatus] || brackets.single;
    let tax = 0;
    let marginalRate = 0;

    for (const bracket of statusBrackets) {
      if (taxableIncome <= bracket.min) break;
      const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
      tax += taxableInBracket * bracket.rate;
      marginalRate = bracket.rate;
    }

    return { tax: Math.round(tax), bracket: `${(marginalRate * 100).toFixed(0)}%` };
  };

  // Get standard deduction for filing status
  const getStandardDeduction = (filingStatus: string): number => {
    const deductions: Record<string, number> = {
      single: 14600,
      married_joint: 29200,
      married_separate: 14600,
      head_of_household: 21900,
      widow: 29200,
    };
    return deductions[filingStatus] || 14600;
  };

  // Initialize editable estimate from current data (or defaults if no profile)
  const initializeEditableEstimate = () => {
    // Use profile filing status if available, otherwise default to single
    const filingStatus = profile?.filing_status || 'single';

    const totalWithholding = incomeEntries.reduce((sum, e) => sum + (e.withholding || 0), 0);
    const standardDed = getStandardDeduction(filingStatus);
    const useItemized = totalDeductions > standardDed;
    const deductionToUse = useItemized ? totalDeductions : standardDed;
    const agi = totalIncome;
    const taxableIncome = Math.max(0, agi - deductionToUse);
    const { tax: federalTax, bracket } = calculateFederalTax(taxableIncome, filingStatus);

    // Self-employment income and tax (15.3% of 92.35% of SE income)
    const seIncome = incomeEntries
      .filter(e => e.income_type === 'self_employment')
      .reduce((sum, e) => sum + e.amount, 0);
    const seTax = Math.round(seIncome * 0.9235 * 0.153);

    const totalTax = federalTax + seTax;
    const effectiveRate = totalIncome > 0 ? totalTax / totalIncome : 0;
    const balanceDue = totalTax - totalWithholding;

    setEditableEstimate({
      filing_status: filingStatus,
      gross_income: totalIncome,
      above_the_line_deductions: 0,
      itemized_deductions: totalDeductions,
      use_itemized: useItemized,
      federal_withholding: totalWithholding,
      state_withholding: 0,
      estimated_payments: 0,
      self_employment_income: seIncome,
      agi: agi,
      taxable_income: taxableIncome,
      federal_tax: federalTax,
      self_employment_tax: seTax,
      total_tax: totalTax,
      effective_rate: effectiveRate,
      marginal_bracket: bracket,
      balance_due: balanceDue,
    });
    setEstimateModified(false);
  };

  // Recalculate computed fields when user changes editable values
  const recalculateEstimate = (updates: Partial<EditableEstimate>) => {
    if (!editableEstimate) return;

    const updated = { ...editableEstimate, ...updates };

    // Use the editable filing status
    const filingStatus = updated.filing_status || 'single';

    // Calculate AGI
    updated.agi = updated.gross_income - updated.above_the_line_deductions;

    // Determine deduction to use
    const standardDed = getStandardDeduction(filingStatus);
    const deductionToUse = updated.use_itemized ? updated.itemized_deductions : standardDed;

    // Calculate taxable income
    updated.taxable_income = Math.max(0, updated.agi - deductionToUse);

    // Calculate federal tax
    const { tax, bracket } = calculateFederalTax(updated.taxable_income, filingStatus);
    updated.federal_tax = tax;
    updated.marginal_bracket = bracket;

    // Calculate self-employment tax (15.3% of 92.35% of SE income)
    updated.self_employment_tax = Math.round(updated.self_employment_income * 0.9235 * 0.153);

    // Total tax
    updated.total_tax = updated.federal_tax + updated.self_employment_tax;

    // Effective rate
    updated.effective_rate = updated.gross_income > 0 ? updated.total_tax / updated.gross_income : 0;

    // Balance due (tax - withholding - estimated payments)
    updated.balance_due = updated.total_tax - updated.federal_withholding - updated.state_withholding - updated.estimated_payments;

    setEditableEstimate(updated);
    setEstimateModified(true);
  };

  // Handle editable field change
  const handleEstimateFieldChange = (field: keyof EditableEstimate, value: number | boolean | string) => {
    recalculateEstimate({ [field]: value });
  };

  // Upload handler for importing tax data from CSV/PDF
  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setError(null);
      // Show loading overlay with file-specific message
      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      setLoadingOverlay({
        visible: true,
        message: t('importing', 'Importing Tax Data...'),
        subMessage: isPdf
          ? t('importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
          : t('importingCsvHint', 'Analyzing and extracting tax information'),
      });

      const response = await apiClient.post('/tax-planning/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Hide loading overlay
      setLoadingOverlay({ visible: false, message: '' });

      let message = response.data.message || 'Import successful';
      if (response.data.imported_income_count !== undefined) {
        message += ` (${response.data.imported_income_count} income entries, ${response.data.imported_deduction_count || 0} deductions)`;
      }
      if (response.data.extraction_metadata) {
        const meta = response.data.extraction_metadata;
        if (meta.pages_ocred > 0) {
          message += ` - ${meta.pages_ocred} pages OCR processed`;
        }
      }

      // Success - no alert needed, just reload data silently
      // Reload all data
      await loadProfile();
      if (profile) {
        await loadIncomeEntries();
        await loadDeductionEntries();
        await loadTaxEstimate();
      }
    } catch (err: any) {
      setLoadingOverlay({ visible: false, message: '' });
      setError(err.response?.data?.detail || 'Failed to import file');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Clear all tax data
  const handleClearAllData = async () => {
    if (!window.confirm(t('confirmClearAll', 'Are you sure you want to clear ALL tax planning data? This cannot be undone.'))) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.delete('/tax-planning/clear-all');
      alert(response.data.message || 'All data cleared');
      setProfile(null);
      setIncomeEntries([]);
      setDeductionEntries([]);
      setTaxEstimate(null);
      setQuarterlyEstimates([]);
      setDocumentChecklist({});
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  // Generate export content for tax summary
  const generateExportContent = (): string => {
    // Use edited values if in edit mode, otherwise use profile data
    const useEditedValues = isEditingEstimate && editableEstimate;

    // If no profile and no editable estimate, cannot generate
    if (!profile && !editableEstimate) return '';

    const filingStatus = useEditedValues ? editableEstimate.filing_status : (profile?.filing_status || 'single');
    const filingStatusName = getFilingStatusName(filingStatus);
    const standardDed = getStandardDeduction(filingStatus);
    const taxYear = profile?.tax_year || new Date().getFullYear();

    let content = `Tax Planning Summary\n`;
    content += `====================\n\n`;
    content += `Tax Year: ${taxYear}\n`;
    content += `Filing Status: ${filingStatusName}\n`;
    if (profile) {
      content += `Dependents: ${profile.dependents}\n`;
      content += `Self-Employed: ${profile.is_self_employed ? 'Yes' : 'No'}\n`;
    }
    if (useEditedValues) {
      content += `\n** Values have been manually adjusted **\n`;
    }
    content += `\n`;

    // Income Summary
    content += `## Income Summary\n`;
    content += `-----------------\n`;
    if (useEditedValues) {
      content += `Gross Income: ${formatCurrency(editableEstimate.gross_income)}\n`;
      content += `Above-the-Line Deductions: ${formatCurrency(editableEstimate.above_the_line_deductions)}\n`;
    } else {
      incomeEntries.forEach(entry => {
        content += `- ${entry.income_type.replace(/_/g, ' ')}: ${formatCurrency(entry.amount)}`;
        if (entry.description) content += ` (${entry.description})`;
        if (entry.withholding > 0) content += ` - Withholding: ${formatCurrency(entry.withholding)}`;
        content += `\n`;
      });
      content += `\nTotal Income: ${formatCurrency(totalIncome)}\n`;
    }
    content += `\n`;

    // Deductions Summary
    content += `## Deductions Summary\n`;
    content += `--------------------\n`;
    if (useEditedValues) {
      const deductionType = editableEstimate.use_itemized ? 'Itemized' : 'Standard';
      const deductionAmount = editableEstimate.use_itemized ? editableEstimate.itemized_deductions : standardDed;
      content += `Using ${deductionType} Deduction: ${formatCurrency(deductionAmount)}\n`;
      if (editableEstimate.use_itemized) {
        content += `(Standard Deduction would be: ${formatCurrency(standardDed)})\n`;
      }
    } else {
      deductionEntries.forEach(entry => {
        content += `- ${entry.category.replace(/_/g, ' ')}: ${formatCurrency(entry.amount)}`;
        if (entry.description) content += ` (${entry.description})`;
        content += `\n`;
      });
      content += `\nTotal Deductions: ${formatCurrency(totalDeductions)}\n`;
    }
    content += `\n`;

    // Tax Estimate - use edited values or original
    content += `## Tax Estimate\n`;
    content += `---------------\n`;

    if (useEditedValues) {
      content += `Gross Income: ${formatCurrency(editableEstimate.gross_income)}\n`;
      content += `Less Above-the-Line Deductions: ${formatCurrency(editableEstimate.above_the_line_deductions)}\n`;
      content += `Adjusted Gross Income (AGI): ${formatCurrency(editableEstimate.agi)}\n`;
      content += `${editableEstimate.use_itemized ? 'Itemized' : 'Standard'} Deduction: ${formatCurrency(editableEstimate.use_itemized ? editableEstimate.itemized_deductions : standardDed)}\n`;
      content += `Taxable Income: ${formatCurrency(editableEstimate.taxable_income)}\n`;
      content += `Federal Income Tax: ${formatCurrency(editableEstimate.federal_tax)}\n`;
      if (editableEstimate.self_employment_income > 0) {
        content += `Self-Employment Income: ${formatCurrency(editableEstimate.self_employment_income)}\n`;
        content += `Self-Employment Tax (15.3%): ${formatCurrency(editableEstimate.self_employment_tax)}\n`;
      }
      content += `\nTotal Estimated Tax: ${formatCurrency(editableEstimate.total_tax)}\n`;
      content += `Effective Tax Rate: ${formatPercent(editableEstimate.effective_rate)}\n`;
      content += `Marginal Tax Bracket: ${editableEstimate.marginal_bracket}\n`;

      // Payments & Withholding section
      content += `\n## Payments & Withholding\n`;
      content += `-------------------------\n`;
      content += `Federal Withholding: ${formatCurrency(editableEstimate.federal_withholding)}\n`;
      if (editableEstimate.state_withholding > 0) {
        content += `State Withholding: ${formatCurrency(editableEstimate.state_withholding)}\n`;
      }
      if (editableEstimate.estimated_payments > 0) {
        content += `Estimated Tax Payments: ${formatCurrency(editableEstimate.estimated_payments)}\n`;
      }
      const totalPayments = editableEstimate.federal_withholding + editableEstimate.state_withholding + editableEstimate.estimated_payments;
      content += `Total Payments: ${formatCurrency(totalPayments)}\n`;

      // Balance Due / Refund
      content += `\n## Balance\n`;
      content += `----------\n`;
      if (editableEstimate.balance_due >= 0) {
        content += `Amount Due: ${formatCurrency(editableEstimate.balance_due)}\n`;
      } else {
        content += `Estimated Refund: ${formatCurrency(Math.abs(editableEstimate.balance_due))}\n`;
      }
    } else if (taxEstimate) {
      content += `Gross Income: ${formatCurrency(taxEstimate.total_income)}\n`;
      content += `Adjusted Gross Income (AGI): ${formatCurrency(taxEstimate.agi)}\n`;
      content += `${taxEstimate.using_itemized ? 'Itemized' : 'Standard'} Deduction: ${formatCurrency(taxEstimate.using_itemized ? taxEstimate.itemized_deductions : taxEstimate.standard_deduction)}\n`;
      content += `Taxable Income: ${formatCurrency(taxEstimate.taxable_income)}\n`;
      content += `Federal Income Tax: ${formatCurrency(taxEstimate.federal_tax)}\n`;
      if (taxEstimate.self_employment_tax > 0) {
        content += `Self-Employment Tax: ${formatCurrency(taxEstimate.self_employment_tax)}\n`;
      }
      content += `\nTotal Estimated Tax: ${formatCurrency(taxEstimate.total_tax)}\n`;
      content += `Effective Tax Rate: ${formatPercent(taxEstimate.effective_tax_rate)}\n`;
      content += `Marginal Tax Bracket: ${taxEstimate.marginal_bracket}\n`;
    }

    content += `\n---\nGenerated by Auxilio Finance - ${new Date().toLocaleString()}\n`;
    content += `\nDisclaimer: This is for informational purposes only and does not constitute tax advice.\n`;

    return content;
  };

  // Handle export
  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') return;
    const content = generateExportContent();
    if (!content) return;

    const title = `Tax Planning Summary - ${profile?.tax_year || new Date().getFullYear()}`;

    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const response = await apiClient.post('/export/document', {
        content: content,
        title: title,
        format: formatMap[format],
      }, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export document:', err);
      setError('Failed to export document');
    }
  };

  // Handle print
  const handlePrint = () => {
    const content = generateExportContent();
    if (!content) return;

    const title = `Tax Planning Summary - ${profile?.tax_year || new Date().getFullYear()}`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
              h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
              h2 { color: #1e40af; margin-top: 20px; }
              pre { white-space: pre-wrap; font-family: inherit; }
            </style>
          </head>
          <body>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const totalIncome = incomeEntries.reduce((sum, e) => sum + e.amount, 0);
  const totalDeductions = deductionEntries.reduce((sum, e) => sum + e.amount, 0);

  // Styles
  const cardClass = 'rounded-xl shadow-lg p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700';

  const tabClass = (isActive: boolean) => `
    px-4 py-2 rounded-lg font-medium transition-colors
    ${isActive
      ? 'bg-blue-600 text-white'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
    }
  `;

  const inputClass = 'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  const selectClass = 'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Loading Overlay for long-running operations */}
      <LoadingOverlay
        isVisible={loadingOverlay.visible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <CalculatorIcon className="h-8 w-8 text-green-500" />
              {t('title')}
            </h1>
            <p className={`mt-2 text-gray-600 dark:text-gray-400`}>
              {t('subtitle')}
            </p>
          </div>

          {/* Icon Bar - Import/Export/Clear */}
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.CSV,.pdf,.PDF,application/pdf,text/csv"
              onChange={handleImportFile}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('importFile', 'Import from CSV or PDF (tax return, brokerage statement, etc.)')}
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={handlePrint}
              disabled={!profile}
              showPrint={true}
            />
            <button
              onClick={handleClearAllData}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('clearAllData', 'Clear all tax planning data')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* No Profile State */}
        {!profile && (
          <div className={cardClass}>
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('noProfile')}</h3>
              <p className={`mb-6 text-gray-600 dark:text-gray-400`}>
                {t('noProfileDescription')}
              </p>
              <button
                onClick={() => setShowProfileModal(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
              >
                <PlusIcon className="h-5 w-5" />
                {t('createProfile')}
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {profile && (
          <>
            {/* Profile Summary Card */}
            <div className={`${cardClass} mb-6`}>
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold mb-2">
                    {t('taxYear', { year: profile.tax_year })}
                  </h2>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className={`px-3 py-1 rounded-full ${
                      'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      {getFilingStatusName(profile.filing_status)}
                    </span>
                    <span className={`px-3 py-1 rounded-full ${
                      'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      {t('dependents', { count: profile.dependents })}
                    </span>
                    {profile.is_self_employed && (
                      <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                        {t('selfEmployed')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setProfileForm({
                      tax_year: profile.tax_year,
                      filing_status: profile.filing_status,
                      dependents: profile.dependents,
                      is_self_employed: profile.is_self_employed,
                      estimated_annual_income: profile.estimated_annual_income,
                    });
                    setShowProfileModal(true);
                  }}
                  className="text-blue-500 hover:text-blue-600"
                >
                  {t('editProfile')}
                </button>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className={cardClass}>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                      {t('totalIncome')}
                    </p>
                    <p className="text-xl font-bold">{formatCurrency(totalIncome)}</p>
                  </div>
                </div>
              </div>

              <div className={cardClass}>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <DocumentCheckIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                      {t('totalDeductions')}
                    </p>
                    <p className="text-xl font-bold">{formatCurrency(totalDeductions)}</p>
                  </div>
                </div>
              </div>

              <div className={cardClass}>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <BanknotesIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                      {t('estimatedTax')}
                    </p>
                    <p className="text-xl font-bold">
                      {taxEstimate ? formatCurrency(taxEstimate.total_tax) : '--'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={cardClass}>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <CalculatorIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                      {t('effectiveRate')}
                    </p>
                    <p className="text-xl font-bold">
                      {taxEstimate ? formatPercent(taxEstimate.effective_tax_rate) : '--'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={tabClass(activeTab === 'overview')}
              >
                {t('tabs.overview')}
              </button>
              <button
                onClick={() => setActiveTab('income')}
                className={tabClass(activeTab === 'income')}
              >
                {t('tabs.income')}
              </button>
              <button
                onClick={() => setActiveTab('deductions')}
                className={tabClass(activeTab === 'deductions')}
              >
                {t('tabs.deductions')}
              </button>
              <button
                onClick={() => {
                  setActiveTab('estimate');
                  loadQuarterlyEstimates();
                }}
                className={tabClass(activeTab === 'estimate')}
              >
                {t('tabs.estimate')}
              </button>
            </div>

            {/* Tab Content */}
            <div className={cardClass}>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">{t('documentChecklist')}</h3>

                  {Object.entries(documentChecklist.checklist || {}).map(([category, items]) => (
                    <div key={category} className="mb-4">
                      <button
                        onClick={() => toggleCategory(category)}
                        className={`w-full flex justify-between items-center p-3 rounded-lg ${
                          'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        <span className="font-medium capitalize">{category.replace(/_/g, ' ')}</span>
                        {expandedCategories.has(category) ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>

                      {expandedCategories.has(category) && (
                        <div className="mt-2 pl-4 space-y-2">
                          {(items as string[]).map((item, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center gap-2 p-2 rounded ${
                                'bg-gray-50 dark:bg-gray-800'
                              }`}
                            >
                              <input type="checkbox" className="rounded" />
                              <span className="text-sm">{item}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Tax Deadlines */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">{t('importantDeadlines')}</h3>
                    <div className="space-y-3">
                      {(documentChecklist.deadlines || []).map((deadline: any, idx: number) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            deadline.is_past
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 dark:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <CalendarIcon className="h-5 w-5" />
                            <span>{deadline.description}</span>
                          </div>
                          <span className="font-medium">{deadline.date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Income Tab */}
              {activeTab === 'income' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">{t('incomeEntries')}</h3>
                    <button
                      onClick={() => setShowIncomeModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      {t('addIncome')}
                    </button>
                  </div>

                  {incomeEntries.length === 0 ? (
                    <div className="text-center py-8">
                      <CurrencyDollarIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className={'text-gray-600 dark:text-gray-400'}>
                        {t('noIncome')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={'border-gray-200 dark:border-gray-700'}>
                            <th className="text-left p-3">{t('type')}</th>
                            <th className="text-left p-3">{t('description')}</th>
                            <th className="text-right p-3">{t('amount')}</th>
                            <th className="text-right p-3">{t('withholding')}</th>
                            <th className="text-center p-3">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {incomeEntries.map(entry => (
                            <tr
                              key={entry.id}
                              className={`border-t ${
                                'border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              <td className="p-3 capitalize">{entry.income_type.replace(/_/g, ' ')}</td>
                              <td className="p-3">{entry.description}</td>
                              <td className="p-3 text-right font-medium">{formatCurrency(entry.amount)}</td>
                              <td className="p-3 text-right">
                                {entry.withholding > 0 ? formatCurrency(entry.withholding) : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => handleEditIncome(entry)}
                                    className="p-2 text-gray-500 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title={t('edit') || 'Edit'}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteIncome(entry.id)}
                                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title={t('delete') || 'Delete'}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className={`border-t-2 ${'border-gray-300 dark:border-gray-600'}`}>
                            <td colSpan={2} className="p-3 font-bold">{t('totalIncome')}</td>
                            <td className="p-3 text-right font-bold">{formatCurrency(totalIncome)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Deductions Tab */}
              {activeTab === 'deductions' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">{t('deductionEntries')}</h3>
                    <button
                      onClick={() => setShowDeductionModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      {t('addDeduction')}
                    </button>
                  </div>

                  {/* Standard vs Itemized Info */}
                  {taxEstimate && (
                    <div className={`mb-4 p-4 rounded-lg ${
                      'bg-blue-50 dark:bg-gray-700'
                    }`}>
                      <div className="flex items-start gap-2">
                        <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div>
                          <p className="font-medium">
                            {taxEstimate.using_itemized ? t('usingItemized') : t('usingStandard')}
                          </p>
                          <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                            {t('standardDeduction')}: {formatCurrency(taxEstimate.standard_deduction)} |{' '}
                            {t('itemizedTotal')}: {formatCurrency(taxEstimate.itemized_deductions)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {deductionEntries.length === 0 ? (
                    <div className="text-center py-8">
                      <DocumentCheckIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className={'text-gray-600 dark:text-gray-400'}>
                        {t('noDeductions')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={'border-gray-200 dark:border-gray-700'}>
                            <th className="text-left p-3">{t('category')}</th>
                            <th className="text-left p-3">{t('description')}</th>
                            <th className="text-right p-3">{t('amount')}</th>
                            <th className="text-center p-3">{t('status')}</th>
                            <th className="text-center p-3">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deductionEntries.map(entry => (
                            <tr
                              key={entry.id}
                              className={`border-t ${
                                'border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              <td className="p-3">
                                <span className="capitalize">{entry.category.replace(/_/g, ' ')}</span>
                                {entry.is_above_the_line && (
                                  <span className="ml-2 text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                                    {t('aboveTheLine')}
                                  </span>
                                )}
                              </td>
                              <td className="p-3">{entry.description}</td>
                              <td className="p-3 text-right font-medium">{formatCurrency(entry.amount)}</td>
                              <td className="p-3 text-center">
                                {entry.documentation_status ? (
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    entry.documentation_status === 'verified'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : entry.documentation_status === 'pending'
                                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                  }`}>
                                    {t(`docStatus.${entry.documentation_status}`) || entry.documentation_status}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">-</span>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => handleEditDeduction(entry)}
                                    className="p-2 text-gray-500 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title={t('edit') || 'Edit'}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDeduction(entry.id)}
                                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title={t('delete') || 'Delete'}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className={`border-t-2 ${'border-gray-300 dark:border-gray-600'}`}>
                            <td colSpan={2} className="p-3 font-bold">{t('totalDeductions')}</td>
                            <td className="p-3 text-right font-bold">{formatCurrency(totalDeductions)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Estimate Tab */}
              {activeTab === 'estimate' && (profile || editableEstimate) && (
                <div>
                  {/* Header with Edit/Reset buttons */}
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">{t('taxEstimateDetails')}</h3>
                    <div className="flex gap-2">
                      {!isEditingEstimate ? (
                        <button
                          onClick={() => {
                            initializeEditableEstimate();
                            setIsEditingEstimate(true);
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors"
                        >
                          <PencilIcon className="h-4 w-4" />
                          {t('editValues', 'Edit Values')}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              initializeEditableEstimate();
                            }}
                            className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 rounded-lg transition-colors"
                          >
                            {t('resetToOriginal', 'Reset to Original')}
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingEstimate(false);
                              setEditableEstimate(null);
                            }}
                            className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 rounded-lg transition-colors"
                          >
                            {t('exitEditMode', 'Exit Edit Mode')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Modified indicator */}
                  {isEditingEstimate && estimateModified && (
                    <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2 text-sm">
                      <InformationCircleIcon className="h-5 w-5 text-yellow-600" />
                      <span className="text-yellow-800 dark:text-yellow-200">
                        {t('estimateModified', 'Values have been modified. Export to save your updated tax estimate.')}
                      </span>
                    </div>
                  )}

                  {/* Tax Calculation Breakdown - Editable or Read-only */}
                  {isEditingEstimate && editableEstimate ? (
                    /* EDITABLE MODE */
                    <div className="space-y-4 mb-8">
                      {/* Editable: Filing Status */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('filingStatus')}</span>
                        <select
                          value={editableEstimate.filing_status}
                          onChange={(e) => handleEstimateFieldChange('filing_status', e.target.value)}
                          className="px-3 py-1 rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                        >
                          {filingStatuses.map(status => (
                            <option key={status.id} value={status.id}>{status.name}</option>
                          ))}
                          {filingStatuses.length === 0 && (
                            <>
                              <option value="single">Single</option>
                              <option value="married_joint">Married Filing Jointly</option>
                              <option value="married_separate">Married Filing Separately</option>
                              <option value="head_of_household">Head of Household</option>
                            </>
                          )}
                        </select>
                      </div>

                      {/* Editable: Gross Income */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('grossIncome')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 dark:text-gray-500">$</span>
                          <input
                            type="number"
                            value={editableEstimate.gross_income}
                            onChange={(e) => handleEstimateFieldChange('gross_income', parseFloat(e.target.value) || 0)}
                            className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Editable: Above-the-Line Deductions */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('aboveTheLineDeductions')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-red-400">-$</span>
                          <input
                            type="number"
                            value={editableEstimate.above_the_line_deductions}
                            onChange={(e) => handleEstimateFieldChange('above_the_line_deductions', parseFloat(e.target.value) || 0)}
                            className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Computed: AGI */}
                      <div className={`flex justify-between items-center py-2 border-t border-gray-200 dark:border-gray-700`}>
                        <span className="font-medium">{t('adjustedGrossIncome')}</span>
                        <span className="font-medium">{formatCurrency(editableEstimate.agi)}</span>
                      </div>

                      {/* Toggle: Use Itemized vs Standard */}
                      <div className="flex justify-between items-center py-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="useItemized"
                            checked={editableEstimate.use_itemized}
                            onChange={(e) => handleEstimateFieldChange('use_itemized', e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor="useItemized" className="text-sm">
                            {t('useItemizedDeductions', 'Use Itemized Deductions')}
                          </label>
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('standardDeduction')}: {formatCurrency(getStandardDeduction(editableEstimate.filing_status))}
                        </span>
                      </div>

                      {/* Editable: Itemized Deductions (if using itemized) */}
                      {editableEstimate.use_itemized && (
                        <div className="flex justify-between items-center py-2 pl-6">
                          <span>{t('itemizedDeductions')}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-red-400">-$</span>
                            <input
                              type="number"
                              value={editableEstimate.itemized_deductions}
                              onChange={(e) => handleEstimateFieldChange('itemized_deductions', parseFloat(e.target.value) || 0)}
                              className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}

                      {/* Computed: Taxable Income */}
                      <div className={`flex justify-between items-center py-2 border-t border-gray-200 dark:border-gray-700`}>
                        <span className="font-medium">{t('taxableIncome')}</span>
                        <span className="font-medium">{formatCurrency(editableEstimate.taxable_income)}</span>
                      </div>

                      {/* Computed: Federal Income Tax */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('federalIncomeTax')}</span>
                        <span className="font-medium">{formatCurrency(editableEstimate.federal_tax)}</span>
                      </div>

                      {/* Editable: Self-Employment Income */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('selfEmploymentIncome', 'Self-Employment Income')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 dark:text-gray-500">$</span>
                          <input
                            type="number"
                            value={editableEstimate.self_employment_income}
                            onChange={(e) => handleEstimateFieldChange('self_employment_income', parseFloat(e.target.value) || 0)}
                            className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Computed: Self-Employment Tax */}
                      {editableEstimate.self_employment_income > 0 && (
                        <div className="flex justify-between items-center py-2 pl-6 text-sm text-gray-600 dark:text-gray-400">
                          <span>{t('selfEmploymentTax')} (15.3%)</span>
                          <span>{formatCurrency(editableEstimate.self_employment_tax)}</span>
                        </div>
                      )}

                      {/* Computed: Total Estimated Tax */}
                      <div className={`flex justify-between items-center py-3 border-t-2 border-gray-300 dark:border-gray-600`}>
                        <span className="text-lg font-bold">{t('totalEstimatedTax')}</span>
                        <span className="text-lg font-bold text-blue-600">
                          {formatCurrency(editableEstimate.total_tax)}
                        </span>
                      </div>

                      {/* Divider for Payments Section */}
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-md font-semibold mb-3">{t('paymentsAndWithholding', 'Payments & Withholding')}</h4>
                      </div>

                      {/* Editable: Federal Withholding */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('federalWithholding', 'Federal Withholding (from W-2s)')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">-$</span>
                          <input
                            type="number"
                            value={editableEstimate.federal_withholding}
                            onChange={(e) => handleEstimateFieldChange('federal_withholding', parseFloat(e.target.value) || 0)}
                            className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Editable: State Withholding */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('stateWithholding', 'State Withholding')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">-$</span>
                          <input
                            type="number"
                            value={editableEstimate.state_withholding}
                            onChange={(e) => handleEstimateFieldChange('state_withholding', parseFloat(e.target.value) || 0)}
                            className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Editable: Estimated Payments */}
                      <div className="flex justify-between items-center py-2">
                        <span>{t('estimatedPaymentsMade', 'Estimated Tax Payments Made')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">-$</span>
                          <input
                            type="number"
                            value={editableEstimate.estimated_payments}
                            onChange={(e) => handleEstimateFieldChange('estimated_payments', parseFloat(e.target.value) || 0)}
                            className="w-32 px-2 py-1 text-right rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Computed: Balance Due / Refund */}
                      <div className={`flex justify-between items-center py-3 border-t-2 border-gray-300 dark:border-gray-600`}>
                        <span className="text-lg font-bold">
                          {editableEstimate.balance_due >= 0 ? t('balanceDue', 'Balance Due') : t('estimatedRefund', 'Estimated Refund')}
                        </span>
                        <span className={`text-lg font-bold ${editableEstimate.balance_due >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {editableEstimate.balance_due >= 0 ? '' : '-'}{formatCurrency(Math.abs(editableEstimate.balance_due))}
                        </span>
                      </div>
                    </div>
                  ) : taxEstimate ? (
                    /* READ-ONLY MODE (original display) */
                    <div className="space-y-3 mb-8">
                      <div className="flex justify-between py-2">
                        <span>{t('grossIncome')}</span>
                        <span className="font-medium">{formatCurrency(taxEstimate.total_income)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span>{t('aboveTheLineDeductions')}</span>
                        <span className="font-medium text-red-500">
                          -{formatCurrency(taxEstimate.above_the_line_deductions)}
                        </span>
                      </div>
                      <div className={`flex justify-between py-2 border-t border-gray-200 dark:border-gray-700`}>
                        <span className="font-medium">{t('adjustedGrossIncome')}</span>
                        <span className="font-medium">{formatCurrency(taxEstimate.agi)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span>
                          {taxEstimate.using_itemized ? t('itemizedDeductions') : t('standardDeduction')}
                        </span>
                        <span className="font-medium text-red-500">
                          -{formatCurrency(taxEstimate.using_itemized ? taxEstimate.itemized_deductions : taxEstimate.standard_deduction)}
                        </span>
                      </div>
                      <div className={`flex justify-between py-2 border-t border-gray-200 dark:border-gray-700`}>
                        <span className="font-medium">{t('taxableIncome')}</span>
                        <span className="font-medium">{formatCurrency(taxEstimate.taxable_income)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span>{t('federalIncomeTax')}</span>
                        <span className="font-medium">{formatCurrency(taxEstimate.federal_tax)}</span>
                      </div>
                      {taxEstimate.self_employment_tax > 0 && (
                        <div className="flex justify-between py-2">
                          <span>{t('selfEmploymentTax')}</span>
                          <span className="font-medium">{formatCurrency(taxEstimate.self_employment_tax)}</span>
                        </div>
                      )}
                      <div className={`flex justify-between py-3 border-t-2 border-gray-300 dark:border-gray-600`}>
                        <span className="text-lg font-bold">{t('totalEstimatedTax')}</span>
                        <span className="text-lg font-bold text-blue-600">
                          {formatCurrency(taxEstimate.total_tax)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CalculatorIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className={'text-gray-600 dark:text-gray-400'}>
                        {t('noEstimate')}
                      </p>
                    </div>
                  )}

                  {/* Marginal Bracket - show in both modes */}
                  {(isEditingEstimate ? editableEstimate : taxEstimate) && (
                    <div className={`p-4 rounded-lg mb-8 bg-gray-100 dark:bg-gray-700`}>
                      <p className="text-sm">
                        <span className={'text-gray-600 dark:text-gray-400'}>
                          {t('marginalBracket')}:{' '}
                        </span>
                        <span className="font-bold">
                          {isEditingEstimate && editableEstimate
                            ? editableEstimate.marginal_bracket
                            : taxEstimate?.marginal_bracket}
                        </span>
                        {' | '}
                        <span className={'text-gray-600 dark:text-gray-400'}>
                          {t('effectiveRate')}:{' '}
                        </span>
                        <span className="font-bold">
                          {formatPercent(
                            isEditingEstimate && editableEstimate
                              ? editableEstimate.effective_rate
                              : (taxEstimate?.effective_tax_rate || 0)
                          )}
                        </span>
                      </p>
                    </div>
                  )}

                  {/* Quarterly Estimates (for self-employed) - only in read-only mode */}
                  {!isEditingEstimate && profile?.is_self_employed && quarterlyEstimates.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold mb-4">{t('quarterlyPayments')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {quarterlyEstimates.map(q => (
                          <div
                            key={q.quarter}
                            className={`p-4 rounded-lg ${
                              q.is_past_due
                                ? 'bg-red-100 border border-red-300'
                                : 'bg-gray-100 dark:bg-gray-700'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {q.is_past_due ? (
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                              ) : (
                                <ClockIcon className="h-5 w-5 text-gray-400" />
                              )}
                              <span className="font-medium">Q{q.quarter}</span>
                            </div>
                            <p className="text-lg font-bold">{formatCurrency(q.amount_due)}</p>
                            <p className={`text-sm ${
                              q.is_past_due ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {t('dueDate')}: {q.due_date}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'estimate' && !profile && !editableEstimate && (
                <div className="text-center py-8">
                  <CalculatorIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className={'text-gray-600 dark:text-gray-400 mb-4'}>
                    {t('noEstimate')}
                  </p>
                  <button
                    onClick={() => {
                      initializeEditableEstimate();
                      setIsEditingEstimate(true);
                    }}
                    className="flex items-center gap-1 px-4 py-2 mx-auto text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                    {t('editValues', 'Edit Values')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Disclaimer */}
        <div className={`mt-6 p-4 rounded-lg ${'bg-yellow-50 dark:bg-gray-800'}`}>
          <p className={`text-sm ${'text-yellow-800 dark:text-gray-400'}`}>
            <strong>{t('disclaimer.title')}:</strong> {t('disclaimer.text')}
          </p>
        </div>

        {/* Profile Modal */}
        {showProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-md rounded-xl shadow-xl ${
              'bg-white dark:bg-gray-800'
            }`}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {profile ? t('editProfile') : t('createProfile')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('taxYearLabel')}</label>
                    <select
                      value={profileForm.tax_year}
                      onChange={e => setProfileForm({ ...profileForm, tax_year: parseInt(e.target.value) })}
                      className={selectClass}
                    >
                      {[2024, 2025].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('filingStatus')}</label>
                    <select
                      value={profileForm.filing_status}
                      onChange={e => setProfileForm({ ...profileForm, filing_status: e.target.value })}
                      className={selectClass}
                    >
                      {filingStatuses.map(status => (
                        <option key={status.id} value={status.id}>{status.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('dependentsLabel')}</label>
                    <input
                      type="number"
                      min="0"
                      value={profileForm.dependents}
                      onChange={e => setProfileForm({ ...profileForm, dependents: parseInt(e.target.value) || 0 })}
                      className={inputClass}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="selfEmployed"
                      checked={profileForm.is_self_employed}
                      onChange={e => setProfileForm({ ...profileForm, is_self_employed: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="selfEmployed" className="text-sm">{t('iAmSelfEmployed')}</label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('estimatedAnnualIncome')}</label>
                    <input
                      type="number"
                      min="0"
                      value={profileForm.estimated_annual_income}
                      onChange={e => setProfileForm({ ...profileForm, estimated_annual_income: parseFloat(e.target.value) || 0 })}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className={`px-4 py-2 rounded-lg ${
                      'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? t('saving') : t('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Income Modal */}
        {showIncomeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-md rounded-xl shadow-xl ${
              'bg-white dark:bg-gray-800'
            }`}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {editingIncomeId ? t('editIncome') || 'Edit Income' : t('addIncome')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('incomeType')}</label>
                    <select
                      value={incomeForm.income_type}
                      onChange={e => setIncomeForm({ ...incomeForm, income_type: e.target.value })}
                      className={selectClass}
                    >
                      <option value="w2_wages">{t('incomeTypes.wages')}</option>
                      <option value="self_employment">{t('incomeTypes.selfEmployment')}</option>
                      <option value="interest">{t('incomeTypes.interest')}</option>
                      <option value="dividends">{t('incomeTypes.dividends')}</option>
                      <option value="capital_gains">{t('incomeTypes.capitalGains')}</option>
                      <option value="rental">{t('incomeTypes.rental')}</option>
                      <option value="retirement">{t('incomeTypes.retirement')}</option>
                      <option value="social_security">{t('incomeTypes.socialSecurity')}</option>
                      <option value="other">{t('incomeTypes.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('description')}</label>
                    <input
                      type="text"
                      value={incomeForm.description}
                      onChange={e => setIncomeForm({ ...incomeForm, description: e.target.value })}
                      placeholder={t('descriptionPlaceholder')}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('amount')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={incomeForm.amount}
                      onChange={e => setIncomeForm({ ...incomeForm, amount: parseFloat(e.target.value) || 0 })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('payerName')}</label>
                    <input
                      type="text"
                      value={incomeForm.payer_name}
                      onChange={e => setIncomeForm({ ...incomeForm, payer_name: e.target.value })}
                      placeholder={t('payerNamePlaceholder')}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('withholding')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={incomeForm.withholding}
                      onChange={e => setIncomeForm({ ...incomeForm, withholding: parseFloat(e.target.value) || 0 })}
                      className={inputClass}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isTaxable"
                      checked={incomeForm.is_taxable}
                      onChange={e => setIncomeForm({ ...incomeForm, is_taxable: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="isTaxable" className="text-sm">{t('isTaxable')}</label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowIncomeModal(false);
                      setEditingIncomeId(null);
                      setIncomeForm({
                        income_type: 'w2_wages',
                        description: '',
                        amount: 0,
                        payer_name: '',
                        is_taxable: true,
                        withholding: 0,
                      });
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={editingIncomeId ? handleUpdateIncome : handleAddIncome}
                    disabled={loading || !incomeForm.description || incomeForm.amount <= 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading
                      ? (editingIncomeId ? t('saving') : t('adding'))
                      : (editingIncomeId ? t('save') : t('add'))
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Deduction Modal */}
        {showDeductionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-md rounded-xl shadow-xl ${
              'bg-white dark:bg-gray-800'
            }`}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {editingDeductionId ? t('editDeduction') || 'Edit Deduction' : t('addDeduction')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('category')}</label>
                    <select
                      value={deductionForm.category}
                      onChange={e => setDeductionForm({ ...deductionForm, category: e.target.value })}
                      className={selectClass}
                    >
                      <option value="">{t('selectCategory')}</option>
                      {deductionCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('description')}</label>
                    <input
                      type="text"
                      value={deductionForm.description}
                      onChange={e => setDeductionForm({ ...deductionForm, description: e.target.value })}
                      placeholder={t('descriptionPlaceholder')}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('amount')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={deductionForm.amount}
                      onChange={e => setDeductionForm({ ...deductionForm, amount: parseFloat(e.target.value) || 0 })}
                      className={inputClass}
                    />
                  </div>

                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowDeductionModal(false);
                      setEditingDeductionId(null);
                      setDeductionForm({
                        category: '',
                        description: '',
                        amount: 0,
                      });
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={editingDeductionId ? handleUpdateDeduction : handleAddDeduction}
                    disabled={loading || !deductionForm.category || !deductionForm.description || deductionForm.amount <= 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading
                      ? (editingDeductionId ? t('saving') : t('adding'))
                      : (editingDeductionId ? t('save') : t('add'))
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TaxPlanning;
